/* biome-ignore-all lint/correctness/useExhaustiveDependencies: the local/remote
   scan commits are one-shot effects, intentionally keyed on `scores` (the
   interpreted result) and guarded by refs — other referenced values are stable
   for the lifetime of a single results visit. */
import { allFrequencies, findById } from '@qi/frequency-db';
import type { FaceSummary, Goal, Scan, VoiceSummary } from '@qi/shared';
import { copy } from '@qi/shared';
import { type RadarAxis, ScoreRadar, ScoreRing } from '@qi/ui';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Activity, ChevronDown, Info, Lock, Play, Share2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BodySystemsReadout } from '../components/scan/BodySystemsReadout.tsx';
import { ComplexionReadout } from '../components/scan/ComplexionReadout.tsx';
import { ConfidenceBadge } from '../components/scan/ConfidenceBadge.tsx';
import { FaceComplexionReadout } from '../components/scan/FaceComplexionReadout.tsx';
import { InviteFriends } from '../components/scan/InviteFriends.tsx';
import { PriorityBadge } from '../components/scan/PriorityBadge.tsx';
import { ShareSheetButton } from '../components/scan/ShareButtons.tsx';
import { TcmInterpretation } from '../components/scan/TcmInterpretation.tsx';
import { TongueReadout } from '../components/scan/TongueReadout.tsx';
import { FlowActionBar } from '../components/shell/FlowActionBar.tsx';
import { GuestSavePrompt } from '../components/shell/GuestSavePrompt.tsx';
import { aggregateBodySystems } from '../lib/body-systems.ts';
import { humanizeClusterId } from '../lib/cluster-labels.ts';
import { buildPriorityRanks, priorityIdAt } from '../lib/priority.ts';
import { useCreateScan, useInterpretScan, useMe } from '../lib/queries.ts';
import { frequencyAffinity } from '../lib/resonance-affinity.ts';
import { selectCanRebalance, toTcmScores, useSessionStore } from '../stores/session-store.ts';

export const Route = createFileRoute('/_app/scan/results')({
  beforeLoad: () => {
    // No fabricated data: results only render for a real, completed scan.
    const { resonancePeaks, faceSummary, voiceSummary } = useSessionStore.getState();
    if (resonancePeaks.length === 0 || !faceSummary || !voiceSummary) {
      throw redirect({ to: '/scan/bioscan' });
    }
  },
  component: ResultsPage,
});

interface DerivedPeak {
  id: string;
  score: number;
}

// Sequential red (low) → yellow (mid) → green (high). Hue sweeps 0→130 so the
// colour reads as a continuous scale, not three buckets. For wellness metrics,
// which genuinely are scores.
function scoreColor(v: number): string {
  const t = Math.max(0, Math.min(100, v)) / 100;
  return `hsl(${Math.round(t * 130)} 72% 48%)`;
}

// The resonance peak is AFFINITY to a tone, not a wellness score — a low value
// just means the tone doesn't resonate with you, NOT that anything is wrong. So
// it reads as a plain band, never a /100 gauge.
function affinityBand(v: number): string {
  if (v >= 80) return 'Very high affinity';
  if (v >= 60) return 'High affinity';
  if (v >= 40) return 'Affinity';
  if (v >= 20) return 'Low affinity';
  return 'Very low affinity';
}

// Affinity ramps from a calm muted tone (low) up to aurora (high) — deliberately
// no red, because low affinity isn't a deficit to fix.
function affinityColor(v: number): string {
  const t = Math.max(0, Math.min(100, v)) / 100;
  return `color-mix(in oklab, var(--aurora) ${Math.round(15 + t * 85)}%, var(--ink-3))`;
}

function ResultsPage() {
  const goal = useSessionStore((s) => s.goal);
  const user = useSessionStore((s) => s.user);
  const tier = useSessionStore((s) => s.tier);
  const navigate = useNavigate();
  // Unlock CTAs send the user to the real paywall/checkout — never a silent
  // local tier flip, so they see the actual purchase before anything unlocks.
  const goToPaywall = () => void navigate({ to: '/unlock' });
  const shareUnlocked = useSessionStore((s) => s.shareUnlocked);
  const unlockViaShare = useSessionStore((s) => s.unlockViaShare);
  const me = useMe();
  const faceSummary = useSessionStore((s) => s.faceSummary);
  const voiceSummary = useSessionStore((s) => s.voiceSummary);
  const complexion = useSessionStore((s) => s.complexion);
  const tongueReading = useSessionStore((s) => s.tongueReading);
  const faceComplexion = useSessionStore((s) => s.faceComplexion);
  const tcmInterpretation = useSessionStore((s) => s.tcmInterpretation);
  const storePeaks = useSessionStore((s) => s.resonancePeaks);
  const setLastScan = useSessionStore((s) => s.setLastScan);
  const createScan = useCreateScan();
  const localCommitRef = useRef(false);
  const remoteCommitRef = useRef(false);

  // beforeLoad guarantees a completed scan in the store.
  const peaks = storePeaks;

  const outcome = useMemo(() => {
    return {
      peaks: peaks.slice(0, 3).map((p) => ({
        id: p.frequencyId,
        score: Math.round(p.score),
      })),
    };
  }, [peaks]);

  // Non-null: the route's beforeLoad redirects to /scan/bioscan unless
  // faceSummary, voiceSummary, and resonancePeaks are all present.
  // Wellness scores come from the backend interpretation endpoint — never
  // synthesized on the client.
  const interpret = useInterpretScan({
    faceMetrics: faceSummary as FaceSummary,
    voiceMetrics: voiceSummary as VoiceSummary,
    resonancePeaks: peaks,
  });
  const scores = interpret.data?.scores;

  // Non-null: the route's beforeLoad redirects to /scan/bioscan unless
  // faceSummary, voiceSummary, and resonancePeaks are all present.
  const scanPayload: Omit<Scan, 'id' | 'scores'> = useMemo(
    () => ({
      userId: null,
      startedAt: new Date().toISOString(),
      durationMs: 60_000,
      faceMetrics: faceSummary as FaceSummary,
      voiceMetrics: voiceSummary as VoiceSummary,
      resonancePeaks: peaks.slice(0, 3),
    }),
    [faceSummary, voiceSummary, peaks],
  );

  // Local commit (so Compare has lastScan even for guests) waits for the real
  // interpreted scores before writing.
  useEffect(() => {
    if (localCommitRef.current || !scores) return;
    localCommitRef.current = true;
    setLastScan({
      scanId: `pending-${Date.now()}`,
      scores,
      peakFrequencyIds: outcome.peaks.map((p) => p.id),
      tcm: toTcmScores(tcmInterpretation),
      capturedAt: scanPayload.startedAt,
    });
  }, [scores]);

  // Signed-in users persist the scan; the server derives + returns authoritative
  // scores, which replace the local pending entry.
  useEffect(() => {
    if (!user || remoteCommitRef.current || !scores) return;
    remoteCommitRef.current = true;
    createScan.mutate(scanPayload, {
      onSuccess: (saved) => {
        setLastScan({
          scanId: saved.id,
          scores: saved.scores,
          peakFrequencyIds: outcome.peaks.map((p) => p.id),
          tcm: toTcmScores(tcmInterpretation),
          capturedAt: saved.startedAt,
        });
      },
    });
  }, [user, scores]);

  const reduced = useReducedMotion();
  // Premium is unlocked by the authoritative plan from the API (set in Postgres
  // by Better Auth — and flipped when the user buys PRO on the main webapp) OR
  // by the local demo toggle. So a PRO purchase on the webapp unlocks everything
  // here the moment /v1/me reports plan: 'premium'.
  const isPremium = me.data?.user?.plan === 'premium' || tier === 'premium';
  const locked = !isPremium;
  const intro = reduced
    ? STATIC
    : { initial: { opacity: 0, y: -8 }, animate: { opacity: 1, y: 0 }, transition: REVEAL.intro };
  const body = (delay: number) =>
    reduced
      ? STATIC
      : {
          initial: { opacity: 0, y: 14 },
          animate: { opacity: 1, y: 0 },
          transition: { ...REVEAL.body, delay },
        };

  // The wellness sub-metrics, each a plain name + clinical subtitle. Outward/
  // longer = better, so stressLoad is inverted to "Calm" to keep every metric
  // pointing the same way.
  const metrics = useMemo(
    () =>
      scores
        ? [
            {
              label: 'Focus',
              sub: 'Autonomic regulation',
              value: scores.focus,
              hz: 741,
              measured: 'Read from your facial steadiness and how calm your voice sounded.',
            },
            {
              label: 'Energy',
              sub: 'Cardiovascular drive',
              value: scores.energy,
              hz: 528,
              measured: 'Read from your resonance response and the steadiness of your voice.',
            },
            {
              label: 'Recovery',
              sub: 'Regenerative capacity',
              value: scores.recovery,
              hz: 285,
              measured: 'Read from how relaxed your voice sounded and your resonance response.',
            },
            {
              label: 'Mood',
              sub: 'Limbic balance',
              value: scores.emotionalCoherence,
              hz: 639,
              measured: 'Read from your facial steadiness and an even, stable voice.',
            },
            {
              label: 'Calm',
              sub: 'Endocrine stress-axis',
              value: 100 - scores.stressLoad,
              hz: 432,
              measured: 'Read from the tension and strain picked up in your voice.',
            },
          ]
        : [],
    [scores],
  );
  const canRebalance = useSessionStore(selectCanRebalance);

  // TCM balance + vitality, only when a tongue photo was analysed and the read
  // carries the scores (guards stale pre-schema data persisted in localStorage).
  const tcmScores = useMemo(() => {
    const b = tcmInterpretation?.balance;
    const v = tcmInterpretation?.vitality;
    return Number.isFinite(b) && Number.isFinite(v)
      ? { balance: b as number, vitality: v as number }
      : null;
  }, [tcmInterpretation]);

  // The full set of TCM metric bars. Each is dropped when its score is missing
  // (older scans persisted before these fields existed still render cleanly).
  const tcmMetrics = useMemo(() => {
    if (!tcmInterpretation) return [];
    const defs = [
      {
        label: 'Balance',
        sub: 'Yin / yang harmony',
        value: tcmInterpretation.balance,
        measured: 'Read from your tongue and complexion — how evenly yin and yang appear.',
      },
      {
        label: 'Vitality',
        sub: 'Face & tongue health',
        value: tcmInterpretation.vitality,
        measured: 'Read from your facial glow and the health of your tongue.',
      },
      {
        label: 'Heat',
        sub: 'Thermal balance',
        value: tcmInterpretation.heat,
        measured: 'How comfortably warm vs overheated your tongue and complexion read.',
      },
      {
        label: 'Fluids',
        sub: 'Hydration & moisture',
        value: tcmInterpretation.fluids,
        measured: 'How well-moistened your tongue and skin look.',
      },
      {
        label: 'Circulation',
        sub: 'Blood & qi flow',
        value: tcmInterpretation.circulation,
        measured: 'How freely colour and vitality move through your complexion.',
      },
      {
        label: 'Organ harmony',
        sub: 'Whole-system balance',
        value: tcmInterpretation.organHarmony,
        measured: 'How evenly the facial zones and tongue map across body systems.',
      },
    ];
    return defs.filter((d) => Number.isFinite(d.value));
  }, [tcmInterpretation]);

  // One combined radar — the wellness metrics plus the TCM balance + vitality
  // axes when present, so the whole picture reads in a single diagram.
  const radarAxes: RadarAxis[] = useMemo(() => {
    const base = metrics.map((m) => ({ label: m.label, value: m.value }));
    if (tcmScores) {
      base.push(
        { label: 'Balance', value: tcmScores.balance },
        { label: 'Vitality', value: tcmScores.vitality },
      );
    }
    return base;
  }, [metrics, tcmScores]);

  // Headline resonance is a composite of every wellness sub-metric, plus the
  // TCM balance + vitality reads when present — not just the single strongest
  // sweep peak. That top peak lives in the frequency section.
  const composite = useMemo(() => {
    if (!scores) return 0;
    const parts = [
      scores.focus,
      scores.energy,
      scores.recovery,
      scores.emotionalCoherence,
      100 - scores.stressLoad,
    ];
    if (tcmScores) parts.push(tcmScores.balance, tcmScores.vitality);
    return Math.round(parts.reduce((sum, v) => sum + v, 0) / parts.length);
  }, [scores, tcmScores]);

  // Body systems pooled from the face + tongue zone reads, shared by the Body
  // systems panel and the interpretation's priority ranking.
  const bodySystems = useMemo(
    () => aggregateBodySystems(faceComplexion?.regions ?? [], tongueReading?.regions ?? []),
    [faceComplexion, tongueReading],
  );

  // Pooled rebalancing priority across the wellness metrics and body systems —
  // the weakest few are the user's top things to act on. Drives both the
  // weakest-first ordering of the wellness card and the "Top/Second/Third
  // priority" badges spanning both cards.
  const priorityRows = useMemo(
    () => [
      ...metrics.map((m) => ({ id: `wellness:${m.label}`, score: m.value })),
      ...bodySystems.map((s) => ({ id: `system:${s.system}`, score: s.score })),
    ],
    [metrics, bodySystems],
  );
  const priorityRanks = useMemo(() => buildPriorityRanks(priorityRows), [priorityRows]);

  // The free/share play ladder, drawn from the same pooled ranking as the
  // badges: priority 1 plays free, priority 2 unlocks once the user shares.
  // Everything else stays behind the paid unlock.
  const freeId = useMemo(() => priorityIdAt(priorityRows, 0), [priorityRows]);
  const shareId = useMemo(() => priorityIdAt(priorityRows, 1), [priorityRows]);

  // Wellness rows rendered weakest-first so the most in-need metric leads.
  const sortedMetrics = useMemo(() => [...metrics].sort((a, b) => a.value - b.value), [metrics]);

  // The single strongest resonance peak, so the interpretation can always point
  // the user at a frequency / Qi Coil to run.
  const topFrequency = useMemo(() => {
    const top = outcome.peaks[0];
    if (!top) return undefined;
    const freq = findById(top.id);
    return {
      label: freq?.historicalAssociation ?? humanizeClusterId(top.id),
      hz: freq?.frequency ?? null,
    };
  }, [outcome.peaks]);

  // The per-row resonance chord is part of the paid unlock now — the share
  // reward is priority 2, not the resonance chord.
  const solfeggioLocked = locked;

  const [inviteOpen, setInviteOpen] = useState(false);

  // Opaque referral link — carries the userId, never the email (no PII in URLs).
  // Falls back to a plain link when the user isn't signed in.
  const referralUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://qiscan.ai';
    const id = me.data?.user?.id;
    return id ? `${origin}/start?ref=${encodeURIComponent(id)}` : `${origin}/start`;
  }, [me.data?.user?.id]);

  const inviteText = useMemo(() => {
    const toneLabel = topFrequency?.hz
      ? `${topFrequency.hz} Hz`
      : (topFrequency?.label ?? 'my tone');
    return `My Qi resonance read: ${composite}/100, strongest at ${toneLabel}. Scan yours free.`;
  }, [composite, topFrequency]);

  if (interpret.isError) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-6 py-20 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
          We couldn't read your resonance
        </p>
        <p className="text-sm text-ink-3">
          Something interrupted the reading. Check your connection and try again.
        </p>
        <button
          type="button"
          onClick={() => interpret.refetch()}
          className="rounded-sm border border-hairline bg-surface-3 px-5 py-2.5 text-sm text-ink-0 transition-colors hover:border-aurora/60"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!scores) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-20 text-center font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
        Interpreting your resonance…
      </div>
    );
  }

  return (
    <>
      <FlowActionBar
        back={{ to: '/dashboard', label: 'Back to dashboard' }}
        primary={{ to: '/rebalance', label: 'Start rebalance' }}
      />
      <div className="mx-auto max-w-7xl space-y-10 px-6 pb-28 pt-6">
        <motion.header className="space-y-2 text-center" {...intro}>
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Results</p>
          <h1 className="font-display text-4xl text-ink-0">{copy.scan.resultsCue}</h1>
        </motion.header>

        {/* Headline: overall resonance on its own, as the top-line score. */}
        <motion.section
          className="overflow-hidden rounded-lg border border-aurora/30 bg-surface-1"
          style={{ boxShadow: 'var(--glow-aurora)' }}
          {...(reduced
            ? STATIC
            : {
                initial: { opacity: 0, scale: 0.98 },
                animate: { opacity: 1, scale: 1 },
                transition: { ...REVEAL.body, delay: 0.08 },
              })}
        >
          <header className="flex items-center justify-between gap-3 border-b border-hairline bg-surface-2 px-6 py-4">
            <span className="flex items-center gap-2">
              <Activity size={16} strokeWidth={1.8} className="text-aurora" />
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
                Overall resonance
              </p>
            </span>
          </header>
          <div className="grid items-center gap-8 px-6 py-8 md:grid-cols-2">
            <div className="flex flex-col items-center gap-3">
              <ScoreRing score={composite} size={220} showScale />
              <p className="max-w-[34ch] text-center text-xs text-ink-3">
                A composite of every signal below
                {tcmScores ? ', including your TCM balance and vitality' : ''} — how coherently you
                read together.
              </p>
            </div>
            <div className="mx-auto w-full max-w-[320px]">
              <ScoreRadar axes={radarAxes} />
            </div>
          </div>
          <div className="flex justify-center border-t border-hairline px-6 py-4">
            <ShareProtocol resonance={composite} peaks={outcome.peaks} goal={goal} />
          </div>
        </motion.section>

        {/* Plain-language overview directly under the headline score — the
            conclusion first, before the detailed metric bars below. */}
        {tcmInterpretation && (
          <motion.section {...body(0.12)}>
            <TcmInterpretation
              interpretation={tcmInterpretation}
              wellness={{
                focus: scores.focus,
                energy: scores.energy,
                recovery: scores.recovery,
                mood: scores.emotionalCoherence,
                calm: 100 - scores.stressLoad,
              }}
              frequency={topFrequency}
              bodySystems={bodySystems}
              locked={locked}
              onUnlock={goToPaywall}
            />
          </motion.section>
        )}

        {/* Wellness sub-metrics: radar + color-coded bars (red low → green high). */}
        <motion.section
          className="overflow-hidden rounded-lg border border-hairline bg-surface-1"
          {...body(0.16)}
        >
          <header className="flex items-center gap-2 border-b border-hairline bg-surface-2 px-6 py-4">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Wellness metrics
            </p>
          </header>
          <div className="space-y-6 px-6 py-8">
            {sortedMetrics.map((m, idx) => {
              const id = `wellness:${m.label}`;
              return (
                <MetricBar
                  key={m.label}
                  label={m.label}
                  sub={m.sub}
                  value={m.value}
                  measured={m.measured}
                  align="left"
                  delay={0.12 + idx * 0.05}
                  hz={m.hz}
                  focusId={id}
                  rebalanceLocked={
                    !canRebalance && id !== freeId && !(shareUnlocked && id === shareId)
                  }
                  shareLocked={!canRebalance && !shareUnlocked && id === shareId}
                  onShare={() => setInviteOpen(true)}
                  priorityRank={priorityRanks.get(id)}
                />
              );
            })}
          </div>
          <DetailedReadings
            face={faceSummary as FaceSummary}
            voice={voiceSummary as VoiceSummary}
            locked={locked}
            onUnlock={goToPaywall}
          />
        </motion.section>

        {(tongueReading || faceComplexion || complexion || tcmInterpretation) && (
          <motion.section className="space-y-4" {...body(0.34)}>
            {(tcmMetrics.length > 0 || tongueReading || faceComplexion || complexion) && (
              <section className="overflow-hidden rounded-lg border border-aurora/30 bg-surface-1">
                <header className="flex items-center gap-2 border-b border-hairline bg-surface-2 px-6 py-4">
                  <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                    TCM metrics
                  </p>
                </header>
                {tcmMetrics.length > 0 && (
                  <div className="grid gap-x-10 gap-y-5 px-6 py-8 sm:grid-cols-2">
                    {tcmMetrics.map((m, idx) => (
                      <MetricBar
                        key={m.label}
                        label={m.label}
                        sub={m.sub}
                        value={m.value}
                        measured={m.measured}
                        align={idx % 2 === 0 ? 'right' : 'left'}
                        delay={0.12 + idx * 0.05}
                      />
                    ))}
                  </div>
                )}
                {(tongueReading || faceComplexion || complexion) && (
                  <details className="group border-t border-hairline">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4 text-sm text-ink-1 transition-colors hover:text-ink-0">
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-2">
                        Detailed readings
                      </span>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.8}
                        className="text-ink-3 transition-transform group-open:rotate-180"
                      />
                    </summary>
                    <div className="space-y-4 px-6 pb-8">
                      {(faceComplexion || complexion) && (
                        <section className="overflow-hidden rounded-lg border border-aurora/30 bg-surface-1">
                          <header className="flex items-center justify-between gap-2 border-b border-hairline bg-surface-2 px-6 py-4">
                            <span className="flex items-center gap-2">
                              <Sparkles size={16} strokeWidth={1.8} className="text-aurora" />
                              <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">
                                Complexion read
                              </p>
                            </span>
                            {faceComplexion && (
                              <ConfidenceBadge confidence={faceComplexion.confidence} />
                            )}
                          </header>
                          <div className="divide-y divide-hairline">
                            {faceComplexion && (
                              <FaceComplexionReadout reading={faceComplexion} embedded />
                            )}
                            {complexion && <ComplexionReadout reading={complexion} embedded />}
                          </div>
                          <p className="border-t border-hairline px-6 py-4 text-[11px] leading-relaxed text-ink-3">
                            A relative, tradition-based facial-complexion read for wellness
                            exploration — not a measurement of any organ or a medical diagnosis.
                            Lighting and image quality affect the result.
                          </p>
                        </section>
                      )}
                      {tongueReading && <TongueReadout reading={tongueReading} />}
                    </div>
                  </details>
                )}
              </section>
            )}
            <BodySystemsReadout
              systems={bodySystems}
              locked={locked}
              onUnlock={goToPaywall}
              priorityRanks={priorityRanks}
            />
          </motion.section>
        )}

        {/* Resonance frequencies: each tone your scan responded to, with its own
            rebalance chord you can play inline (single-active). Playback is gated
            until you share or go premium — the free viral-loop reward. */}
        <motion.section
          className="overflow-hidden rounded-lg border border-hairline bg-surface-1"
          {...body(0.44)}
        >
          <header className="flex items-center gap-2 border-b border-hairline bg-surface-2 px-6 py-4">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Resonance metrics
            </p>
          </header>
          <div className="space-y-6 px-6 py-8">
            <div className="space-y-5">
              {(locked ? outcome.peaks.slice(0, 1) : outcome.peaks).map((peak, idx) => (
                <FrequencyRow
                  key={peak.id}
                  id={peak.id}
                  value={peak.score}
                  delay={0.12 + idx * 0.06}
                  locked={locked}
                  chordLocked={solfeggioLocked}
                />
              ))}
            </div>
            {locked && (
              <button
                type="button"
                onClick={goToPaywall}
                className="flex w-full items-center justify-center gap-2 rounded-sm border border-violet/40 bg-surface-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-violet transition-colors hover:border-violet/70"
              >
                <Lock size={13} strokeWidth={1.8} />
                Unlock your full resonance sweep &amp; affinities
                {outcome.peaks.length > 1 ? ` (+${outcome.peaks.length - 1} more)` : ''}
              </button>
            )}
          </div>
          <ResonanceReadings />
          <p className="border-t border-hairline px-6 py-4 text-[11px] leading-relaxed text-ink-3">
            Resonance affinities are pattern-based associations from the solfeggio, chakra and Rife
            traditions — not measurements of an organ or a medical diagnosis. We surface
            associations; you decide what to try.
          </p>
        </motion.section>

        {/* Highest-leverage conversion moment: the user just got a personalised
            protocol. Capture an email before they leave so they can come back
            to it. Once signed in this collapses into a soft confirmation. */}
        <motion.div {...body(0.48)}>
          <GuestSavePrompt
            headline="Save this protocol"
            description="Sign in to keep this resonance map and pick it back up tomorrow. We'll email you a one-time link."
            cta="Save my protocol"
            savedHeadline="Protocol saved"
            savedDescription="Find it on your dashboard and come back to it whenever you need a reset."
          />
        </motion.div>
      </div>
      <InviteFriends
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => {
          unlockViaShare();
          setInviteOpen(false);
        }}
        referralUrl={referralUrl}
        shareText={inviteText}
      />
    </>
  );
}

/**
 * One frequency card in the consolidated bottom section. Basic info is always
 * free: the tone-dotted name, its Hz, and an affinity band (not a score — see
 * affinityBand). The deeper affinity grid (system / organ / chakra) is gated —
 * blurred until premium — so the report shows a taste for free and reserves the
 * detail for unlock.
 */
function FrequencyRow({
  id,
  value,
  delay,
  locked,
  chordLocked,
}: {
  id: string;
  value: number;
  delay: number;
  locked: boolean;
  chordLocked: boolean;
}) {
  const reduced = useReducedMotion();
  const pct = Math.max(0, Math.min(100, value));
  const color = affinityColor(pct);
  const freq = findById(id);
  const label = freq?.historicalAssociation ?? humanizeClusterId(id);
  const affinity = frequencyAffinity(id);
  return (
    <motion.div
      className="space-y-4 rounded-lg border border-hairline bg-surface-2/40 p-4 sm:p-5"
      initial={reduced ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {/* Header: tone name + Hz on the left, affinity chip on the right. The
          chip and the name dot share the affinity colour so the eye links them
          across the row — and the chip reads as an intentional status, not the
          leftover floating label it was before. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-2 text-sm text-ink-0">
            <span
              aria-hidden="true"
              className="inline-block size-2 shrink-0 rounded-full"
              style={{ background: color }}
            />
            <span className="truncate">{label}</span>
          </span>
          {freq && (
            <span className="pl-4 font-mono text-[11px] tracking-wide text-ink-3">
              {freq.frequency} Hz
            </span>
          )}
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-pill border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em]"
          style={{ color, borderColor: `color-mix(in oklab, ${color} 40%, transparent)` }}
        >
          {affinityBand(pct)}
        </span>
      </div>

      <p className="text-sm leading-relaxed text-ink-1">
        Resonates toward your {affinity.organs.join(' and ')}.
      </p>

      <dl
        className={`grid gap-x-4 gap-y-2 sm:grid-cols-2 ${locked ? 'select-none blur-[5px]' : ''}`}
        aria-hidden={locked}
      >
        <AffinityField term="System" value={affinity.system} />
        <AffinityField term="Chakra" value={affinity.chakra} />
      </dl>

      {freq && (
        <div className="flex items-center justify-between gap-3 border-t border-hairline pt-3.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            Golden chord
          </span>
          {chordLocked ? (
            <StartRebalanceLink focusId={`resonance:${id}`} locked />
          ) : (
            <StartRebalanceLink focusId={`resonance:${id}`} />
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Hands one item off to the Rebalance screen, cued to play first. Playback lives
 * on the dedicated Rebalance screen — not inline on the report — so the row just
 * deep-links there with a `focus` cue.
 */
function StartRebalanceLink({
  focusId,
  locked,
  shareLocked,
  onShare,
  emphasized,
}: {
  focusId: string;
  locked?: boolean;
  shareLocked?: boolean;
  onShare?: () => void;
  emphasized?: boolean;
}) {
  // The second-priority row is one share away — offer that free path here rather
  // than the paywall, so the user can earn the track instead of paying for it.
  if (shareLocked) {
    return (
      <button
        type="button"
        onClick={onShare}
        className="flex shrink-0 items-center gap-2 rounded-sm border border-aurora/50 bg-surface-1 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-aurora transition-colors hover:border-aurora/80"
      >
        <Share2 size={13} strokeWidth={1.8} />
        Share to unlock
      </button>
    );
  }
  // A locked row sends the user to the paywall to unlock — priority 1 plays free
  // and priority 2 unlocks via share; everything else is behind the $4.99 unlock.
  if (locked) {
    return (
      <Link
        to="/unlock"
        className="flex shrink-0 items-center gap-2 rounded-sm border border-violet/40 bg-surface-1 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-violet transition-colors hover:border-violet/70"
      >
        <Lock size={13} strokeWidth={1.8} />
        Unlock to play
      </Link>
    );
  }
  // A priority row gets a solid violet→aurora button so the user is drawn to
  // play it ahead of the outline buttons on lower-priority rows.
  if (emphasized) {
    return (
      <Link
        to="/rebalance"
        search={{ focus: focusId, mode: 'top3' }}
        className="flex shrink-0 items-center gap-2 rounded-sm px-3.5 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-abyss transition-opacity hover:opacity-90"
        style={{
          background: 'linear-gradient(90deg, var(--violet), var(--aurora))',
          boxShadow: '0 0 12px color-mix(in oklab, var(--aurora) 35%, transparent)',
        }}
      >
        <Play size={13} strokeWidth={2.2} />
        Start Rebalance
      </Link>
    );
  }
  return (
    <Link
      to="/rebalance"
      search={{ focus: focusId, mode: 'top3' }}
      className="flex shrink-0 items-center gap-2 rounded-sm border border-aurora/40 bg-surface-1 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-aurora transition-colors hover:border-aurora/70"
    >
      <Play size={13} strokeWidth={1.8} />
      Start Rebalance
    </Link>
  );
}

/**
 * Expandable reference under the resonance metrics — the full solfeggio set with
 * each tone's traditional affinity. Always open to everyone (general lore, not
 * the user's gated personal read), so the curious reader can see what every
 * frequency resonates toward, not just their own peaks.
 */
function ResonanceReadings() {
  const solfeggios = allFrequencies().filter((f) => f.category === 'solfeggio');
  return (
    <details className="group border-t border-hairline">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4 text-sm text-ink-1 transition-colors hover:text-ink-0">
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-2">
          Detailed readings
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.8}
          className="text-ink-3 transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="divide-y divide-hairline px-6 pb-8">
        {solfeggios.map((f) => {
          const affinity = frequencyAffinity(f.id);
          return (
            <div key={f.id} className="space-y-1 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-ink-0">{f.historicalAssociation ?? f.id}</span>
                <span className="font-mono text-[11px] tabular-nums text-ink-3">
                  {f.frequency} Hz
                </span>
              </div>
              <p className="text-sm leading-relaxed text-ink-1">
                Resonates toward your {affinity.organs.join(' and ')}.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                {affinity.system} · {affinity.chakra} chakra
              </p>
            </div>
          );
        })}
      </div>
    </details>
  );
}

function AffinityField({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">{term}</dt>
      <dd className="text-xs text-ink-1">{value}</dd>
    </div>
  );
}

/**
 * Expandable "actual readings" card under the wellness metrics. Shows the raw
 * captured face + voice signals that feed the scores — collapsed by default so
 * the report stays clean for the casual reader and detailed for the curious one.
 */
function DetailedReadings({
  face,
  voice,
  locked,
  onUnlock,
}: {
  face: FaceSummary;
  voice: VoiceSummary;
  locked: boolean;
  onUnlock: () => void;
}) {
  const faceRows: { label: string; value: string }[] = [
    { label: 'Blink rate', value: `${face.blinkRate.toFixed(1)} bpm` },
    { label: 'Facial stability', value: face.facialStability.toFixed(2) },
    { label: 'Breathing', value: `${(face.breathingRateHz * 60).toFixed(0)} /min` },
    { label: 'Jaw tension', value: face.jawTension.toFixed(2) },
    { label: 'Brow tension', value: face.foreheadTension.toFixed(2) },
    ...(face.pupilResponse !== undefined
      ? [{ label: 'Pupil response', value: face.pupilResponse.toFixed(2) }]
      : []),
  ];
  const voiceRows: { label: string; value: string }[] = [
    { label: 'Pitch', value: `${voice.pitchHz.toFixed(0)} Hz` },
    { label: 'Voice stability', value: `${Math.round(voice.stability)}/100` },
    { label: 'Voice stress', value: `${Math.round(voice.stressScore)}/100` },
    { label: 'Breathiness', value: `${Math.round(voice.breathiness)}/100` },
    { label: 'Jitter', value: `${(voice.jitter * 100).toFixed(1)}%` },
    { label: 'Shimmer', value: `${(voice.shimmer * 100).toFixed(1)}%` },
  ];
  if (locked) {
    return (
      <button
        type="button"
        onClick={onUnlock}
        className="flex w-full items-center justify-between gap-3 border-t border-hairline px-6 py-4 text-left transition-colors hover:bg-surface-2"
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-2">
          Detailed readings
        </span>
        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-violet">
          <Lock size={13} strokeWidth={1.8} />
          Unlock with Premium
        </span>
      </button>
    );
  }
  return (
    <details className="group border-t border-hairline">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-4 text-sm text-ink-1 transition-colors hover:text-ink-0">
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-2">
          Detailed readings
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.8}
          className="text-ink-3 transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="grid gap-x-10 gap-y-6 px-6 pb-8 sm:grid-cols-2">
        <ReadingGroup title="Face" rows={faceRows} />
        <ReadingGroup title="Voice" rows={voiceRows} />
      </div>
    </details>
  );
}

function ReadingGroup({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: string }[];
}) {
  return (
    <div className="space-y-1">
      <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-aurora">{title}</p>
      <dl className="divide-y divide-hairline">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4 py-2">
            <dt className="text-sm text-ink-1">{r.label}</dt>
            <dd className="font-mono text-[12px] tabular-nums text-ink-0">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * A single labelled wellness score rendered as an animated bar. The fill sweeps
 * from 0 to its value so the report reads as a live readout; respects reduced
 * motion by rendering the final width immediately.
 */
function MetricBar({
  label,
  sub,
  value,
  measured,
  align,
  delay,
  hz,
  focusId,
  rebalanceLocked,
  shareLocked,
  onShare,
  priorityRank,
}: {
  label: string;
  sub: string;
  value: number;
  measured: string;
  align: 'left' | 'right';
  delay: number;
  hz?: number;
  focusId?: string;
  rebalanceLocked?: boolean;
  shareLocked?: boolean;
  onShare?: () => void;
  priorityRank?: number;
}) {
  const reduced = useReducedMotion();
  const pct = Math.max(0, Math.min(100, value));
  const color = scoreColor(pct);
  const prioritized = priorityRank != null;
  return (
    <div
      className={
        prioritized
          ? 'space-y-1.5 rounded-md border border-violet/30 bg-[color-mix(in_oklab,var(--violet)_5%,transparent)] px-4 py-3.5'
          : 'space-y-1.5'
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm text-ink-0">
          {label}
          <MeasuredTip label={label} measured={measured} align={align} />
          {priorityRank != null && <PriorityBadge rank={priorityRank} />}
        </span>
        <span className="font-mono text-sm tabular-nums text-ink-0">
          {Math.round(pct)}
          <span className="ml-0.5 text-[11px] text-ink-3">/100</span>
        </span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">{sub}</p>
      <span className="block h-1.5 overflow-hidden rounded-pill bg-surface-3">
        <motion.span
          className="block h-full rounded-pill"
          style={{
            background: color,
            boxShadow: `0 0 8px color-mix(in oklab, ${color} 50%, transparent)`,
          }}
          initial={reduced ? false : { width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
        />
      </span>
      {hz != null && focusId && (
        <div className="flex items-center justify-end gap-3 pt-1.5">
          <StartRebalanceLink
            focusId={focusId}
            locked={rebalanceLocked}
            shareLocked={shareLocked}
            onShare={onShare}
            emphasized={prioritized}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Small "i" affordance next to a wellness metric. On hover or keyboard focus it
 * reveals what signals were measured for that score — never the formula/weights.
 */
function MeasuredTip({
  label,
  measured,
  align,
}: {
  label: string;
  measured: string;
  align: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  // Open toward the inside of the card so the popover never clips against the
  // section's overflow-hidden edge: left column opens right, right column left.
  const sidePos = align === 'right' ? 'left-0' : 'right-0';
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={`What ${label} measures`}
        className="text-ink-3 transition-colors hover:text-aurora focus-visible:text-aurora"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Info size={13} strokeWidth={1.8} />
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute bottom-full z-10 mb-2 w-52 rounded-lg border border-hairline bg-surface-3 px-3 py-2 text-xs leading-relaxed text-ink-1 shadow-lg ${sidePos}`}
        >
          {measured}
        </span>
      )}
    </span>
  );
}

// Reduced-motion shape — drops every initial offset so motion.* still renders
// but skips the entrance animation entirely.
const STATIC = { initial: false, animate: { opacity: 1 } } as const;

const REVEAL = {
  intro: { duration: 0.34, ease: [0.22, 1, 0.36, 1] as const },
  body: { duration: 0.36, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * Share affordance for the results screen — the post-scan moment where the user
 * has just "arrived" and is most likely to share. Renders a row of per-platform
 * share targets, each prefilled with the user's read; nothing sends on their
 * behalf.
 */
function ShareProtocol({
  resonance,
  peaks,
  goal,
}: {
  resonance: number;
  peaks: DerivedPeak[];
  goal: Goal | null;
}) {
  const peakNames = peaks
    .map((p) => findById(p.id)?.historicalAssociation ?? humanizeClusterId(p.id))
    .join(', ');
  const goalLine = goal ? ` for ${goal.replace('-', ' ')}` : '';
  const text = `My Qi resonance read${goalLine}: ${resonance}/100, strongest in ${peakNames}. Scan yours free.`;
  const url = typeof window !== 'undefined' ? window.location.origin : 'https://qiscan.ai';

  return <ShareSheetButton url={url} text={text} subject="My Qi resonance read" />;
}
